import React, { useState, useEffect } from 'react';
import { Save, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';

const BulkAttendance = () => {
  const [employees, setEmployees] = useState([]);
  const [costCentres, setCostCentres] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return localStorage.getItem('attendance_month') || new Date().toISOString().slice(0, 7);
  });
  const [monthlyData, setMonthlyData] = useState({});
  const [saving, setSaving] = useState(false);
  const [filterCostCentre, setFilterCostCentre] = useState(() => {
    return localStorage.getItem('attendance_cost_centre') || '';
  });

  useEffect(() => {
    localStorage.setItem('attendance_month', selectedMonth);
    fetchData();
  }, [selectedMonth]);

  useEffect(() => {
    localStorage.setItem('attendance_cost_centre', filterCostCentre);
  }, [filterCostCentre]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [empRes, attRes, ccRes] = await Promise.all([
        api.get('/employees?status=Active'),
        api.get(`/attendance?month=${selectedMonth}&_t=${Date.now()}`),
        api.get('/cost-centres'),
      ]);

      setEmployees(empRes.data);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);

      // Aggregate attendance data by employee
      const aggregated = {};
      empRes.data.forEach((emp) => {
        aggregated[emp.id] = {
          days_present: 0,
          total_ot_hours: 0,
          odw_days: 0,
          incentive: 0,
          ta: 0,
          advance: 0,
          deductions: 0,
          weekly_offs: 0,
          paid_leaves: 0,
          lwp: 0,
          ot_comment: '', odw_comment: '', incentive_comment: '', ta_comment: '',
          pl_comment: '', lwp_comment: '', advance_comment: '', deductions_comment: '',
        };
      });

      // Count days present, sum OT hours, sum ODW days, sum financial fields per employee
      attRes.data.forEach((att) => {
        if (aggregated[att.employee_id]) {
          if (att.status === 'Present') {
            aggregated[att.employee_id].days_present += 1;
          } else if (att.status === 'Half-day') {
            aggregated[att.employee_id].days_present += 0.5;
          }
          aggregated[att.employee_id].total_ot_hours += att.ot_hours || 0;
          aggregated[att.employee_id].odw_days += att.odw_days || 0;
          aggregated[att.employee_id].incentive += att.incentive || 0;
          aggregated[att.employee_id].ta += att.ta || 0;
          aggregated[att.employee_id].advance += att.advance || 0;
          aggregated[att.employee_id].deductions += att.deductions || 0;
          aggregated[att.employee_id].weekly_offs += att.weekly_offs || 0;
          aggregated[att.employee_id].paid_leaves += att.paid_leaves || 0;
          aggregated[att.employee_id].lwp += att.lwp || 0;
          // Load comments from first record (day 1)
          if (att.ot_comment) aggregated[att.employee_id].ot_comment = att.ot_comment;
          if (att.odw_comment) aggregated[att.employee_id].odw_comment = att.odw_comment;
          if (att.incentive_comment) aggregated[att.employee_id].incentive_comment = att.incentive_comment;
          if (att.ta_comment) aggregated[att.employee_id].ta_comment = att.ta_comment;
          if (att.pl_comment) aggregated[att.employee_id].pl_comment = att.pl_comment;
          if (att.lwp_comment) aggregated[att.employee_id].lwp_comment = att.lwp_comment;
          if (att.advance_comment) aggregated[att.employee_id].advance_comment = att.advance_comment;
          if (att.deductions_comment) aggregated[att.employee_id].deductions_comment = att.deductions_comment;
        }
      });

      // Convert to string values for inputs
      const empMap = {};
      empRes.data.forEach(e => { empMap[e.id] = e; });
      Object.keys(aggregated).forEach((empId) => {
        const empCycleSundays = getCycleSundays(empMap[empId]);
        aggregated[empId] = {
          days_present: aggregated[empId].days_present > 0 ? String(aggregated[empId].days_present) : '',
          total_ot_hours: aggregated[empId].total_ot_hours > 0 ? String(parseFloat(aggregated[empId].total_ot_hours.toFixed(1))) : '',
          odw_days: aggregated[empId].odw_days > 0 ? String(aggregated[empId].odw_days) : '',
          incentive: aggregated[empId].incentive > 0 ? String(aggregated[empId].incentive) : '',
          ta: aggregated[empId].ta > 0 ? String(aggregated[empId].ta) : '',
          advance: aggregated[empId].advance > 0 ? String(aggregated[empId].advance) : '',
          deductions: aggregated[empId].deductions > 0 ? String(aggregated[empId].deductions) : '',
          weekly_offs: aggregated[empId].weekly_offs > 0 ? String(aggregated[empId].weekly_offs) : String(empCycleSundays),
          paid_leaves: aggregated[empId].paid_leaves > 0 ? String(aggregated[empId].paid_leaves) : '',
          lwp: aggregated[empId].lwp > 0 ? String(aggregated[empId].lwp) : '',
          ot_comment: aggregated[empId].ot_comment || '',
          odw_comment: aggregated[empId].odw_comment || '',
          incentive_comment: aggregated[empId].incentive_comment || '',
          ta_comment: aggregated[empId].ta_comment || '',
          pl_comment: aggregated[empId].pl_comment || '',
          lwp_comment: aggregated[empId].lwp_comment || '',
          advance_comment: aggregated[empId].advance_comment || '',
          deductions_comment: aggregated[empId].deductions_comment || '',
        };
      });

      setMonthlyData(aggregated);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    return new Date(year, month, 0).getDate();
  };

  const getSundaysInMonth = (yearMonth) => {
    const [y, m] = yearMonth.split('-').map(Number);
    const days = new Date(y, m, 0).getDate();
    let sundays = 0;
    for (let d = 1; d <= days; d++) {
      if (new Date(y, m - 1, d).getDay() === 0) sundays++;
    }
    return sundays;
  };

  // Calculate Sundays based on employee's salary cycle period
  const getCycleSundays = (emp) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const cycle = emp?.salary_cycle || '1st to End of Month';
    if (cycle === '16th to 15th') {
      let p_m = m - 1, p_y = y;
      if (p_m <= 0) { p_m = 12; p_y -= 1; }
      const daysInPrev = new Date(p_y, p_m, 0).getDate();
      let sundays = 0;
      for (let d = 16; d <= daysInPrev; d++) {
        if (new Date(p_y, p_m - 1, d).getDay() === 0) sundays++;
      }
      for (let d = 1; d <= 15; d++) {
        if (new Date(y, m - 1, d).getDay() === 0) sundays++;
      }
      return sundays;
    }
    return getSundaysInMonth(selectedMonth);
  };

  const daysInMonth = getDaysInMonth(selectedMonth);
  const defaultWeeklyOffs = getSundaysInMonth(selectedMonth);

  // Calculate cycle days based on employee's salary cycle and selected salary month
  const getCycleDays = (emp) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const cycle = emp.salary_cycle || '1st to End of Month';

    if (cycle === '16th to 15th') {
      // March salary → Feb 16 to Mar 15
      // (previous month) 16th to end + (current month) 1st to 15th
      let p_m = m - 1, p_y = y;
      if (p_m <= 0) { p_m = 12; p_y -= 1; }
      const daysInPrev = new Date(p_y, p_m, 0).getDate(); // days in previous month
      return (daysInPrev - 15) + 15; // from 16th to end + 15
    } else {
      // "1st to End of Month" → current month's total days
      return new Date(y, m, 0).getDate();
    }
  };

  // Get max input days based on employee's salary cycle
  const getMaxDays = (emp) => getCycleDays(emp);

  // Helper to display cycle period label
  const getCyclePeriodLabel = (emp) => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const cycle = emp.salary_cycle || '1st to End of Month';
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    if (cycle === '16th to 15th') {
      let p_m = m - 1, p_y = y;
      if (p_m <= 0) { p_m = 12; p_y -= 1; }
      return `16 ${monthNames[p_m-1]} - 15 ${monthNames[m-1]}`;
    } else {
      const days = new Date(y, m, 0).getDate();
      return `1-${days} ${monthNames[m-1]}`;
    }
  };

  const updateData = (empId, field, value) => {
    setMonthlyData((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const bulkData = [];
      const errors = [];
      
      // Only process employees visible in current filter
      const visibleEmployees = employees.filter(emp => !filterCostCentre || emp.cost_centre_id === filterCostCentre);
      
      visibleEmployees.forEach((emp) => {
        const data = monthlyData[emp.id];
        const daysPresent = parseFloat(data.days_present) || 0;
        const totalOT = parseFloat(data.total_ot_hours) || 0;
        const odwDays = parseFloat(data.odw_days) || 0;
        const incentive = parseFloat(data.incentive) || 0;
        const ta = parseFloat(data.ta) || 0;
        const advance = parseFloat(data.advance) || 0;
        const deductions = parseFloat(data.deductions) || 0;
        const weeklyOffs = parseFloat(data.weekly_offs) || 0;
        const paidLeaves = parseFloat(data.paid_leaves) || 0;
        const lwp = parseFloat(data.lwp) || 0;
        const maxDays = getMaxDays(emp);

        // Validation
        if (daysPresent > maxDays) {
          errors.push(`${emp.name}: Days present (${daysPresent}) cannot exceed ${maxDays} days for ${emp.salary_cycle || '1st to End of Month'} cycle`);
          return;
        }

        if (odwDays > daysPresent) {
          errors.push(`${emp.name}: ODW days (${odwDays}) cannot exceed days present (${daysPresent})`);
          return;
        }

        if (daysPresent > 0) {
          const fullDays = Math.floor(daysPresent);
          const hasHalfDay = daysPresent % 1 !== 0;
          
          // Create full-day attendance records
          for (let day = 1; day <= fullDays && day <= maxDays; day++) {
            bulkData.push({
              employee_id: emp.id,
              date: `${selectedMonth}-${String(day).padStart(2, '0')}`,
              status: 'Present',
              hours_worked: 8,
              ot_hours: day === 1 ? totalOT : 0,
              odw_days: day === 1 ? odwDays : 0,
              is_off_day: false,
              incentive: day === 1 ? incentive : 0,
              ta: day === 1 ? ta : 0,
              advance: day === 1 ? advance : 0,
              deductions: day === 1 ? deductions : 0,
              weekly_offs: day === 1 ? weeklyOffs : 0,
              paid_leaves: day === 1 ? paidLeaves : 0,
              lwp: day === 1 ? lwp : 0,
              ...(day === 1 ? {
                ot_comment: data.ot_comment || '', odw_comment: data.odw_comment || '',
                incentive_comment: data.incentive_comment || '', ta_comment: data.ta_comment || '',
                pl_comment: data.pl_comment || '', lwp_comment: data.lwp_comment || '',
                advance_comment: data.advance_comment || '', deductions_comment: data.deductions_comment || '',
              } : {}),
            });
          }
          
          // Create half-day record if decimal
          if (hasHalfDay && (fullDays + 1) <= maxDays) {
            const halfDayNum = fullDays + 1;
            bulkData.push({
              employee_id: emp.id,
              date: `${selectedMonth}-${String(halfDayNum).padStart(2, '0')}`,
              status: 'Half-day',
              hours_worked: 4,
              ot_hours: fullDays === 0 ? totalOT : 0,
              odw_days: fullDays === 0 ? odwDays : 0,
              is_off_day: false,
              incentive: fullDays === 0 ? incentive : 0,
              ta: fullDays === 0 ? ta : 0,
              advance: fullDays === 0 ? advance : 0,
              deductions: fullDays === 0 ? deductions : 0,
              weekly_offs: fullDays === 0 ? weeklyOffs : 0,
              paid_leaves: fullDays === 0 ? paidLeaves : 0,
              lwp: fullDays === 0 ? lwp : 0,
            });
          }
        }
      });

      if (errors.length > 0) {
        toast.error(errors.join('\n'), { duration: 5000 });
        setSaving(false);
        return;
      }

      // Send ALL visible employee IDs so the backend can clean up old records
      // even for employees whose data was cleared to 0/empty
      const activeEmployeeIds = visibleEmployees.map(emp => emp.id);

      await api.post('/attendance/bulk-save', {
        month: selectedMonth,
        employee_ids: activeEmployeeIds,
        records: bulkData,
      });
      toast.success(`Saved attendance for ${selectedMonth}. ${bulkData.length} records processed.`);
      await fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bulk-attendance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Monthly Attendance</h1>
          <p className="mt-2 text-base text-slate-600">Enter monthly totals for all employees</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-bulk-button">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* Month & Cost Centre Selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div>
          <Label htmlFor="month-select">Select Month</Label>
          <div className="flex items-center gap-3 mt-2">
            <Calendar className="h-5 w-5 text-slate-500" />
            <Input
              id="month-select"
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="max-w-xs"
              data-testid="month-selector"
            />
            <span className="text-sm text-slate-600">{daysInMonth} calendar days</span>
          </div>
        </div>
        <div className="min-w-[200px]">
          <Label htmlFor="cc-filter">Cost Centre</Label>
          <select
            id="cc-filter"
            value={filterCostCentre}
            onChange={(e) => setFilterCostCentre(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring mt-2"
            data-testid="filter-cost-centre-select"
          >
            <option value="">All Cost Centres</option>
            {Object.entries(costCentres).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm font-semibold text-blue-900 mb-2">How to use:</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs text-blue-700">
          <div>• <strong>Days Present:</strong> Total working days in month (e.g., 28, 30, 31)</div>
          <div>• <strong>Total OT Hours:</strong> Sum of all overtime hours for the month (e.g., 40 hours)</div>
          <div>• <strong>ODW Days:</strong> Number of off-days worked (Sundays/holidays, e.g., 2 days)</div>
          <div>• <strong>Incentive:</strong> Performance bonus for the month (₹)</div>
          <div>• <strong>T.A.:</strong> Travel Allowance for the month (₹)</div>
          <div>• <strong>Advance:</strong> Salary advance given (will be deducted, ₹)</div>
          <div>• <strong>Deductions:</strong> Other deductions like fines, damages (₹)</div>
        </div>
        <p className="text-xs text-blue-600 mt-2">Example: Rajesh Kumar → 31 days, 40 OT hours, 1 ODW, ₹5000 incentive, ₹2000 TA, ₹3000 advance, ₹500 deductions</p>
      </div>

      {/* Employee List */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {/* Scroll hint */}
        <div className="bg-indigo-50 border-b border-indigo-200 px-4 py-2 text-xs text-indigo-700 flex items-center gap-2">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
          <span className="font-medium">Scroll horizontally to see all columns →</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1800px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10">Employee</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider sticky left-0 bg-slate-50 z-10" style={{left: '200px'}}>Designation</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-amber-600 uppercase tracking-wider whitespace-nowrap">Per Day (₹)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-violet-600 uppercase tracking-wider whitespace-nowrap">Shift</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Days Present</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap">Weekly Offs</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wider whitespace-nowrap">Paid Leaves</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-red-600 uppercase tracking-wider whitespace-nowrap">LWP</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-cyan-700 uppercase tracking-wider whitespace-nowrap bg-cyan-50">D.O.P.</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Total OT Hours</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">ODW Days</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Incentive (₹)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">T.A. (₹)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Advance (₹)</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">Deductions (₹)</th>
              </tr>
            </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan="15" className="text-center py-12 text-slate-500">
                  No active employees found. Add employees first.
                </td>
              </tr>
            ) : (
              employees.filter(emp => !filterCostCentre || emp.cost_centre_id === filterCostCentre).map((emp, idx) => (
                <tr key={emp.id} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                  <td className="px-4 py-4 sticky left-0 bg-inherit z-10 min-w-[200px]">
                    <p className="text-sm font-semibold text-slate-900">{(emp.name || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</p>
                    <p className="text-xs text-slate-500">{emp.email}</p>
                    <p className="text-xs text-indigo-600 mt-0.5">{emp.salary_cycle || '1st to End of Month'} <span className="text-slate-400">({getCyclePeriodLabel(emp)} • {getCycleDays(emp)}d)</span></p>
                  </td>
                  <td className="px-4 py-4 sticky bg-inherit z-10 min-w-[150px]" style={{left: '200px'}}>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                      {emp.employee_type_name}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono font-semibold text-amber-700" data-testid={`per-day-${emp.id}`}>
                      ₹{(() => {
                        const gross = (emp.basic_salary || 0) + (emp.hra || 0) + (emp.da || 0) + (emp.conv || 0);
                        const cycleDays = getCycleDays(emp);
                        return cycleDays > 0 ? Math.round(gross / cycleDays).toLocaleString('en-IN') : '0';
                      })()}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200" data-testid={`shift-${emp.id}`}>
                      {emp.shift_hours || 8}h
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      type="number"
                      step="0.5"
                      min="0"
                      max={getMaxDays(emp)}
                      placeholder={`Max ${getMaxDays(emp)}`}
                      value={monthlyData[emp.id]?.days_present || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        const maxD = getMaxDays(emp);
                        if (value > maxD) {
                          toast.error(`Cannot exceed ${maxD} days for ${emp.salary_cycle || '1st to End of Month'} cycle`, { duration: 2000 });
                          return;
                        }
                        updateData(emp.id, 'days_present', e.target.value);
                      }}
                      className={`text-center max-w-[120px] mx-auto ${
                        parseFloat(monthlyData[emp.id]?.days_present) > getMaxDays(emp) 
                          ? 'border-red-500 bg-red-50' 
                          : ''
                      }`}
                      data-testid={`days-${emp.id}`}
                    />
                    {parseFloat(monthlyData[emp.id]?.days_present) > getMaxDays(emp) && (
                      <p className="text-xs text-red-600 mt-1 text-center">Max {getMaxDays(emp)}</p>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="6"
                      placeholder="W.Offs"
                      value={monthlyData[emp.id]?.weekly_offs || ''}
                      onChange={(e) => updateData(emp.id, 'weekly_offs', e.target.value)}
                      className="text-center max-w-[100px] mx-auto bg-blue-50 border-blue-200"
                      data-testid={`woffs-${emp.id}`}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.5" min="0" placeholder="PL"
                      value={monthlyData[emp.id]?.paid_leaves || ''}
                      onChange={(e) => updateData(emp.id, 'paid_leaves', e.target.value)}
                      className="text-center max-w-[100px] mx-auto bg-emerald-50 border-emerald-200"
                      data-testid={`pl-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.pl_comment || ''}
                      onChange={(e) => updateData(emp.id, 'pl_comment', e.target.value)}
                      className="w-full max-w-[100px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.5" min="0" placeholder="LWP"
                      value={monthlyData[emp.id]?.lwp || ''}
                      onChange={(e) => updateData(emp.id, 'lwp', e.target.value)}
                      className="text-center max-w-[100px] mx-auto bg-red-50 border-red-200"
                      data-testid={`lwp-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.lwp_comment || ''}
                      onChange={(e) => updateData(emp.id, 'lwp_comment', e.target.value)}
                      className="w-full max-w-[100px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4 bg-cyan-50/40">
                    <div className="text-center font-mono font-bold text-cyan-800 text-sm" data-testid={`dop-${emp.id}`}>
                      {(() => {
                        const d = monthlyData[emp.id] || {};
                        const work = parseFloat(d.days_present) || 0;
                        const woff = parseFloat(d.weekly_offs) || 0;
                        const pl = parseFloat(d.paid_leaves) || 0;
                        const lwpv = parseFloat(d.lwp) || 0;
                        const odw = parseFloat(d.odw_days) || 0;
                        return (work + woff + pl - lwpv + odw) || 0;
                      })()}
                    </div>
                    <div className="text-center text-[10px] text-cyan-600 mt-0.5">W+WO+PL-LWP+ODW</div>
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.5" min="0" placeholder="Total OT"
                      value={monthlyData[emp.id]?.total_ot_hours || ''}
                      onChange={(e) => updateData(emp.id, 'total_ot_hours', e.target.value)}
                      className="text-center max-w-[120px] mx-auto bg-indigo-50 border-indigo-200"
                      data-testid={`ot-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.ot_comment || ''}
                      onChange={(e) => updateData(emp.id, 'ot_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" min="0" max="10" step="0.5" placeholder="ODW days"
                      value={monthlyData[emp.id]?.odw_days || ''}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        const daysPresent = parseFloat(monthlyData[emp.id]?.days_present) || 0;
                        if (value > daysPresent && daysPresent > 0) {
                          toast.error(`ODW days cannot exceed days present (${daysPresent})`, { duration: 2000 });
                          return;
                        }
                        updateData(emp.id, 'odw_days', e.target.value);
                      }}
                      className={`text-center max-w-[120px] mx-auto bg-purple-50 border-purple-200 ${
                        parseFloat(monthlyData[emp.id]?.odw_days) > parseFloat(monthlyData[emp.id]?.days_present || 0) ? 'border-red-500 bg-red-50' : ''
                      }`}
                      data-testid={`odw-${emp.id}`} />
                    {parseFloat(monthlyData[emp.id]?.odw_days) > parseFloat(monthlyData[emp.id]?.days_present || 0) && (
                      <p className="text-xs text-red-600 mt-1 text-center">Cannot exceed days present</p>
                    )}
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.odw_comment || ''}
                      onChange={(e) => updateData(emp.id, 'odw_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.01" min="0" placeholder="Incentive"
                      value={monthlyData[emp.id]?.incentive || ''}
                      onChange={(e) => updateData(emp.id, 'incentive', e.target.value)}
                      className="text-center max-w-[120px] mx-auto bg-emerald-50 border-emerald-200"
                      data-testid={`incentive-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.incentive_comment || ''}
                      onChange={(e) => updateData(emp.id, 'incentive_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.01" min="0" placeholder="T.A."
                      value={monthlyData[emp.id]?.ta || ''}
                      onChange={(e) => updateData(emp.id, 'ta', e.target.value)}
                      className="text-center max-w-[120px] mx-auto bg-blue-50 border-blue-200"
                      data-testid={`ta-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.ta_comment || ''}
                      onChange={(e) => updateData(emp.id, 'ta_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.01" min="0" placeholder="Advance"
                      value={monthlyData[emp.id]?.advance || ''}
                      onChange={(e) => updateData(emp.id, 'advance', e.target.value)}
                      className="text-center max-w-[120px] mx-auto bg-amber-50 border-amber-200"
                      data-testid={`advance-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.advance_comment || ''}
                      onChange={(e) => updateData(emp.id, 'advance_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                  <td className="px-4 py-4">
                    <Input type="number" step="0.01" min="0" placeholder="Deductions"
                      value={monthlyData[emp.id]?.deductions || ''}
                      onChange={(e) => updateData(emp.id, 'deductions', e.target.value)}
                      className="text-center max-w-[120px] mx-auto bg-rose-50 border-rose-200"
                      data-testid={`deductions-${emp.id}`} />
                    <input type="text" placeholder="Comment" value={monthlyData[emp.id]?.deductions_comment || ''}
                      onChange={(e) => updateData(emp.id, 'deductions_comment', e.target.value)}
                      className="w-full max-w-[120px] mx-auto mt-1 text-[11px] px-1.5 py-0.5 border border-slate-200 rounded text-slate-600 block" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export default BulkAttendance;
